import { createContext, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { apiClient } from '../services/api';
import type { TenantBranding } from '../types';
import {
  DEFAULT_BRANDING_CONFIG,
  DEFAULT_DARK_THEME_COLORS,
  DEFAULT_LIGHT_THEME_COLORS,
} from '../constants/brandingDefaults';

interface BrandingContextValue {
  branding: TenantBranding;
  isLoading: boolean;
  error: string | null;
  refetchBranding: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextValue | undefined>(undefined);

const normalizeColor = (color?: string): string | undefined => {
  const value = color?.trim();
  return value ? value : undefined;
};

const mergeBrandingWithDefaults = (branding: TenantBranding | undefined): TenantBranding => {
  return {
    ...DEFAULT_BRANDING_CONFIG,
    ...branding,
    commentColor: normalizeColor(branding?.commentColor) ?? DEFAULT_BRANDING_CONFIG.commentColor,
    internalCommentColor:
      normalizeColor(branding?.internalCommentColor) ??
      DEFAULT_BRANDING_CONFIG.internalCommentColor,
    systemMessageColor:
      normalizeColor(branding?.systemMessageColor) ?? DEFAULT_BRANDING_CONFIG.systemMessageColor,
    changeRecordColor:
      normalizeColor(branding?.changeRecordColor) ?? DEFAULT_BRANDING_CONFIG.changeRecordColor,
    logoUrl: normalizeColor(branding?.logoUrl),
    primaryColor: normalizeColor(branding?.primaryColor),
    secondaryColor: normalizeColor(branding?.secondaryColor),
    accentColor: normalizeColor(branding?.accentColor),
    backgroundColor: normalizeColor(branding?.backgroundColor),
    surfaceColor: normalizeColor(branding?.surfaceColor),
    textColor: normalizeColor(branding?.textColor),
    themeBase: branding?.themeBase === 'dark' ? 'dark' : 'light',
  };
};

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const tenantId = user?.tenantId;

  const brandingQuery = useQuery({
    queryKey: ['tenant-branding', tenantId],
    queryFn: async () => {
      try {
        return await apiClient.getTenantBranding(tenantId!);
      } catch {
        return null;
      }
    },
    enabled: isAuthenticated && !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  const branding = useMemo(
    () => mergeBrandingWithDefaults(brandingQuery.data ?? undefined),
    [brandingQuery.data],
  );

  useEffect(() => {
    const root = document.documentElement;
    const baseThemeDefaults =
      branding.themeBase === 'dark' ? DEFAULT_DARK_THEME_COLORS : DEFAULT_LIGHT_THEME_COLORS;

    root.style.setProperty('--color-change-record-bg', branding.changeRecordColor);
    root.style.setProperty('--color-comment-bg', branding.commentColor);
    root.style.setProperty('--color-internal-comment-bg', branding.internalCommentColor);
    root.style.setProperty('--color-system-message-bg', branding.systemMessageColor);

    root.style.setProperty('--color-primary', branding.primaryColor ?? baseThemeDefaults.primary);
    root.style.setProperty('--color-accent', branding.accentColor ?? baseThemeDefaults.accent);
    root.style.setProperty(
      '--color-topbar-bg',
      branding.secondaryColor ?? branding.surfaceColor ?? baseThemeDefaults.secondary,
    );
    root.style.setProperty(
      '--color-sidebar-bg',
      branding.surfaceColor ?? branding.secondaryColor ?? baseThemeDefaults.surface,
    );
    root.style.setProperty(
      '--color-page-bg',
      branding.backgroundColor ?? baseThemeDefaults.background,
    );
    root.style.setProperty('--color-text', branding.textColor ?? baseThemeDefaults.text);
    window.dispatchEvent(new CustomEvent('tenant-brand-changed'));
  }, [branding]);

  const value: BrandingContextValue = {
    branding,
    isLoading: brandingQuery.isLoading,
    error: brandingQuery.error instanceof Error ? brandingQuery.error.message : null,
    refetchBranding: async () => {
      await brandingQuery.refetch();
    },
  };

  return (
    <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}
